/**
 * Offline Context - Gerencia estado de conexão e modo offline
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initDB, getLocalDBStats, clearExpiredData } from '@/services/indexedDB';
import { syncAll, getSyncStatus, addSyncListener, SyncStatus } from '@/services/syncService';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    status: SyncStatus.IDLE,
    progress: 0,
    total: 0,
    lastSync: null,
    errors: []
  });
  const [localStats, setLocalStats] = useState({
    patients_total: 0,
    patients_pending: 0,
    prescriptions_total: 0,
    prescriptions_draft: 0,
    sync_queue_size: 0
  });
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);

  // ======== CALLBACKS PRIMEIRO (antes dos useEffects que os usam) ========

  const handleOnline = useCallback(async () => {
    console.log('[Offline] Connection restored');
    setIsOnline(true);
    setShowOfflineBanner(false);

    // Atualizar estatísticas primeiro
    const stats = await getLocalDBStats();
    setLocalStats(stats);

    // Tentar sincronizar automaticamente se há itens pendentes
    if (stats.sync_queue_size > 0) {
      console.log('[Offline] Starting auto-sync...', stats.sync_queue_size, 'items');
      setShowOfflineBanner(true); // Mostrar banner de sync
      
      try {
        await syncAll();
        
        // Atualizar estatísticas após sync
        const newStats = await getLocalDBStats();
        setLocalStats(newStats);
        
        if (newStats.sync_queue_size === 0) {
          setShowOfflineBanner(false);
        }
      } catch (error) {
        console.error('[Offline] Auto-sync failed:', error);
      }
    }
  }, []);

  const handleOffline = useCallback(() => {
    console.log('[Offline] Connection lost');
    setIsOnline(false);
    setShowOfflineBanner(true);
  }, []);

  // Sincronizar manualmente
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      return { success: false, message: 'Sem conexão com a internet' };
    }
    
    const result = await syncAll();
    
    // Atualizar estatísticas
    const stats = await getLocalDBStats();
    setLocalStats(stats);
    
    return result;
  }, [isOnline]);

  // ======== EFFECTS (agora podem usar os callbacks definidos acima) ========

  // Inicializar IndexedDB
  useEffect(() => {
    async function init() {
      try {
        await initDB();
        setIsOfflineReady(true);
        console.log('[Offline] IndexedDB initialized');

        // Limpar dados expirados (15 dias)
        await clearExpiredData();

        // Atualizar estatísticas
        const stats = await getLocalDBStats();
        setLocalStats(stats);
      } catch (error) {
        console.error('[Offline] Error initializing IndexedDB:', error);
      }
    }
    init();
  }, []);

  // Registrar Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('[Offline] Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.error('[Offline] Service Worker registration failed:', error);
        });

      // Escutar mensagens do Service Worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'ONLINE') {
          handleOnline();
        } else if (event.data.type === 'OFFLINE') {
          handleOffline();
        } else if (event.data.type === 'SYNC_ALL') {
          syncAll();
        }
      });
    }
  }, [handleOnline, handleOffline]);

  // Monitorar estado de conexão
  useEffect(() => {
    const handleOnlineEvent = () => handleOnline();
    const handleOfflineEvent = () => handleOffline();

    window.addEventListener('online', handleOnlineEvent);
    window.addEventListener('offline', handleOfflineEvent);

    // Verificar conexão periodicamente (a cada 30 segundos)
    const connectionCheck = setInterval(async () => {
      const wasOnline = isOnline;
      const nowOnline = navigator.onLine;
      
      if (!wasOnline && nowOnline) {
        console.log('[Offline] Connection check detected online status');
        handleOnline();
      } else if (wasOnline && !nowOnline) {
        handleOffline();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnlineEvent);
      window.removeEventListener('offline', handleOfflineEvent);
      clearInterval(connectionCheck);
    };
  }, [isOnline, handleOnline, handleOffline]);

  // Escutar mudanças de sync status
  useEffect(() => {
    const unsubscribe = addSyncListener((status) => {
      setSyncStatus(status);
    });
    return unsubscribe;
  }, []);

  // Atualizar estatísticas locais
  const refreshLocalStats = useCallback(async () => {
    const stats = await getLocalDBStats();
    setLocalStats(stats);
    return stats;
  }, []);

  // Verificar se uma funcionalidade requer conexão
  const requiresConnection = useCallback((feature) => {
    const onlineOnlyFeatures = [
      'emit_prescription',    // Emissão final de receita
      'generate_qrcode',      // Geração de QR Code
      'digital_signature',    // Assinatura digital
      'send_whatsapp',        // Envio de WhatsApp
      'legal_validation'      // Validações jurídicas
    ];
    
    return onlineOnlyFeatures.includes(feature);
  }, []);

  // Verificar se pode executar uma ação
  const canPerformAction = useCallback((action) => {
    if (isOnline) return true;
    
    const offlineAllowedActions = [
      'create_patient',
      'edit_patient',
      'create_draft',
      'edit_draft',
      'view_cached_data'
    ];
    
    return offlineAllowedActions.includes(action);
  }, [isOnline]);

  // Fechar banner manualmente
  const dismissBanner = useCallback(() => {
    setShowOfflineBanner(false);
  }, []);

  const value = {
    isOnline,
    isOfflineReady,
    showOfflineBanner,
    dismissBanner,
    syncStatus,
    localStats,
    triggerSync,
    refreshLocalStats,
    requiresConnection,
    canPerformAction
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

// Hook para usar o contexto
export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

// Hook simplificado para verificar conexão
export function useOnlineStatus() {
  const { isOnline } = useOffline();
  return isOnline;
}

export default OfflineContext;
